import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';

// Auth
import LoginPage from './pages/auth/LoginPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import StudentsPage from './pages/admin/StudentsPage';
import TeachersPage from './pages/admin/TeachersPage';
import ClassesPage from './pages/admin/ClassesPage';
import AttendancePage from './pages/admin/AttendancePage';
import MarksPage from './pages/admin/MarksPage';
import SubjectsPage from './pages/admin/SubjectsPage';
import FeesPage from './pages/admin/FeesPage';
import StudentImportPage from './pages/admin/StudentImportPage';
import SettingsPage from './pages/admin/SettingsPage';
import FeeStructurePage from './pages/admin/FeeStructurePage';
import PasswordResetRequestsPage from './pages/admin/PasswordResetRequestsPage';
import PasswordHistoryPage from './pages/admin/PasswordHistoryPage';

// Common Pages (shared by multiple roles)
import HomeworkPage from './pages/common/HomeworkPage';
import EventsPage from './pages/common/EventsPage';

// Teacher Pages
import TeacherDashboard from './pages/teacher/TeacherDashboard';

// Student Pages
import StudentDashboard from './pages/student/StudentDashboard';
import StudentAttendancePage from './pages/student/AttendancePage';
import StudentMarksPage from './pages/student/MarksPage';

// Parent Pages
import ParentDashboard from './pages/parent/ParentDashboard';

// Super Admin Pages
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SuperAdminInvoicesPage from './pages/superadmin/SuperAdminInvoicesPage';
import QuotationsPage from './pages/superadmin/QuotationsPage';
import VendorSettingsPage from './pages/superadmin/VendorSettingsPage';

// Examination Module (Admin + Teacher)
import QuestionBankPage from './pages/examinations/QuestionBankPage';
import CreatePaperPage from './pages/examinations/CreatePaperPage';
import MyPapersPage from './pages/examinations/MyPapersPage';
import ApprovalQueuePage from './pages/examinations/ApprovalQueuePage';
import AuditLogsPage from './pages/examinations/AuditLogsPage';
import SyllabusPage from './pages/examinations/SyllabusPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, skipForceCheck }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner h-10 w-10 text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change before any other route
  if (!skipForceCheck && user.password_change_required) {
    return <Navigate to="/change-password" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={`/${user.role}`} replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner h-10 w-10 text-primary" />
      </div>
    );
  }

  if (user) {
    if (user.password_change_required) {
      return <Navigate to="/change-password" replace />;
    }
    return <Navigate to={`/${user.role}`} replace />;
  }

  return children;
};

// Guard for the change-password page — must be authenticated, no force-redirect loop
const ChangePasswordGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner h-10 w-10 text-primary" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Change password — bypasses force-change gate so users can actually change */}
      <Route path="/change-password" element={
        <ChangePasswordGuard><ChangePasswordPage /></ChangePasswordGuard>
      } />

      <Route path="/admin/password-resets" element={
        <ProtectedRoute allowedRoles={['admin']}><PasswordResetRequestsPage /></ProtectedRoute>
      } />
      <Route path="/admin/password-history" element={
        <ProtectedRoute allowedRoles={['admin']}><PasswordHistoryPage /></ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/students" element={<ProtectedRoute allowedRoles={['admin']}><StudentsPage /></ProtectedRoute>} />
      <Route path="/admin/teachers" element={<ProtectedRoute allowedRoles={['admin']}><TeachersPage /></ProtectedRoute>} />
      <Route path="/admin/classes" element={<ProtectedRoute allowedRoles={['admin']}><ClassesPage /></ProtectedRoute>} />
      <Route path="/admin/subjects" element={<ProtectedRoute allowedRoles={['admin']}><SubjectsPage /></ProtectedRoute>} />
      <Route path="/admin/attendance" element={<ProtectedRoute allowedRoles={['admin']}><AttendancePage /></ProtectedRoute>} />
      <Route path="/admin/marks" element={<ProtectedRoute allowedRoles={['admin']}><MarksPage /></ProtectedRoute>} />
      <Route path="/admin/fees" element={<ProtectedRoute allowedRoles={['admin']}><FeesPage /></ProtectedRoute>} />
      <Route path="/admin/fees/structure" element={<ProtectedRoute allowedRoles={['admin']}><FeeStructurePage /></ProtectedRoute>} />
      <Route path="/admin/import" element={<ProtectedRoute allowedRoles={['admin']}><StudentImportPage /></ProtectedRoute>} />
      <Route path="/admin/homework" element={<ProtectedRoute allowedRoles={['admin']}><HomeworkPage /></ProtectedRoute>} />
      <Route path="/admin/events" element={<ProtectedRoute allowedRoles={['admin']}><EventsPage /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['admin']}><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />

      {/* Super Admin Routes */}
      <Route path="/superadmin" element={<ProtectedRoute allowedRoles={['superadmin']}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/superadmin/schools" element={<ProtectedRoute allowedRoles={['superadmin']}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/superadmin/revenue" element={<ProtectedRoute allowedRoles={['superadmin']}><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path="/superadmin/invoices" element={<ProtectedRoute allowedRoles={['superadmin']}><SuperAdminInvoicesPage /></ProtectedRoute>} />
      <Route path="/superadmin/quotations" element={<ProtectedRoute allowedRoles={['superadmin']}><QuotationsPage /></ProtectedRoute>} />
      <Route path="/superadmin/vendor-settings" element={<ProtectedRoute allowedRoles={['superadmin']}><VendorSettingsPage /></ProtectedRoute>} />

      {/* Teacher Routes */}
      <Route path="/teacher" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/teacher/classes" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/teacher/attendance" element={<ProtectedRoute allowedRoles={['teacher']}><AttendancePage /></ProtectedRoute>} />
      <Route path="/teacher/marks" element={<ProtectedRoute allowedRoles={['teacher']}><MarksPage /></ProtectedRoute>} />
      <Route path="/teacher/students" element={<ProtectedRoute allowedRoles={['teacher']}><StudentsPage /></ProtectedRoute>} />
      <Route path="/teacher/homework" element={<ProtectedRoute allowedRoles={['teacher']}><HomeworkPage /></ProtectedRoute>} />
      <Route path="/teacher/events" element={<ProtectedRoute allowedRoles={['teacher']}><EventsPage /></ProtectedRoute>} />

      {/* Examination Module (Admin + Teacher) */}
      <Route path="/examinations/question-bank" element={
        <ProtectedRoute allowedRoles={['admin', 'teacher']}><QuestionBankPage /></ProtectedRoute>
      } />
      <Route path="/examinations/create-paper" element={
        <ProtectedRoute allowedRoles={['admin', 'teacher']}><CreatePaperPage /></ProtectedRoute>
      } />
      <Route path="/examinations/edit-paper/:paperId" element={
        <ProtectedRoute allowedRoles={['admin', 'teacher']}><CreatePaperPage /></ProtectedRoute>
      } />
      <Route path="/examinations/my-papers" element={
        <ProtectedRoute allowedRoles={['admin', 'teacher']}><MyPapersPage /></ProtectedRoute>
      } />
      <Route path="/examinations/approval-queue" element={
        <ProtectedRoute allowedRoles={['admin', 'teacher']}><ApprovalQueuePage /></ProtectedRoute>
      } />
      <Route path="/examinations/audit-logs" element={
        <ProtectedRoute allowedRoles={['admin', 'teacher']}><AuditLogsPage /></ProtectedRoute>
      } />
      <Route path="/examinations/syllabus" element={
        <ProtectedRoute allowedRoles={['admin', 'teacher']}><SyllabusPage /></ProtectedRoute>
      } />

      {/* Student Routes */}
      <Route path="/student" element={<ProtectedRoute allowedRoles={['student']}><StudentDashboard /></ProtectedRoute>} />
      <Route path="/student/attendance" element={<ProtectedRoute allowedRoles={['student']}><StudentAttendancePage /></ProtectedRoute>} />
      <Route path="/student/marks" element={<ProtectedRoute allowedRoles={['student']}><StudentMarksPage /></ProtectedRoute>} />
      <Route path="/student/marksheets" element={<ProtectedRoute allowedRoles={['student']}><StudentMarksPage /></ProtectedRoute>} />
      <Route path="/student/homework" element={<ProtectedRoute allowedRoles={['student']}><HomeworkPage /></ProtectedRoute>} />
      <Route path="/student/events" element={<ProtectedRoute allowedRoles={['student']}><EventsPage /></ProtectedRoute>} />

      {/* Parent Routes */}
      <Route path="/parent" element={<ProtectedRoute allowedRoles={['parent']}><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/children" element={<ProtectedRoute allowedRoles={['parent']}><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/attendance" element={<ProtectedRoute allowedRoles={['parent']}><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/marks" element={<ProtectedRoute allowedRoles={['parent']}><ParentDashboard /></ProtectedRoute>} />
      <Route path="/parent/homework" element={<ProtectedRoute allowedRoles={['parent']}><HomeworkPage /></ProtectedRoute>} />
      <Route path="/parent/events" element={<ProtectedRoute allowedRoles={['parent']}><EventsPage /></ProtectedRoute>} />

      {/* Default Route */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
